import { createFormContext } from '@mantine/form';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { Descendant } from 'slate';

import { FormAttachment } from 'src/Hooks/Forms/types/formAttachment.types';
import { CreateInstructionDto } from 'src/Services/API/generated';

interface Section {
  title: string;
  content: Descendant[];
  localId: string;
  attachments: FormAttachment[];
}

export interface CreateInstructionFormValues {
  title: string;
  sections: Section[];
  attachment?: FormAttachment;
}

export const EMPTY_PARAGRAPH = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const EMPTY_PARAGRAPH_STRING = JSON.stringify(EMPTY_PARAGRAPH);
const isContentEmpty = (content: Descendant[]) => {
  if (!content || content.length === 0) {
    return true;
  }
  return JSON.stringify(content) === EMPTY_PARAGRAPH_STRING;
};

const isSectionEmpty = (section: Section) => {
  return (
    section.title.trim() === '' &&
    isContentEmpty(section.content) &&
    (!section.attachments || section.attachments.length === 0)
  );
};

export const [CreateInstructionFormProvider, useCreateInstructionFormContext, useForm] =
  createFormContext<
    CreateInstructionFormValues,
    (value: CreateInstructionFormValues) => CreateInstructionDto
  >();

export const useCreateInstructionForm = () => {
  const { t } = useTranslation();

  const form = useForm({
    initialValues: {
      title: '',
      sections: [
        {
          title: '',
          content: EMPTY_PARAGRAPH,
          localId: nanoid(),
          attachments: [] as FormAttachment[],
        },
      ],
    },
    validate: {
      title: (value: string) =>
        value.length < 2 ? t('instructions.invalidTitle', 'Titre invalide') : null,
      sections: {
        title: (value: string, values: CreateInstructionFormValues, path: string) => {
          const sectionIndex = parseInt(path.split('.')[1], 10);
          if (isNaN(sectionIndex)) return null;

          const section = values.sections[sectionIndex];
          if (!section) return null;

          const sectionHasNoContent =
            isContentEmpty(section.content) &&
            (!section.attachments || section.attachments.length === 0);

          if (values.attachment && sectionHasNoContent) {
            return null;
          }

          if (value.length < 2) {
            return t('instructions.invalidTitle', 'Titre invalide');
          }

          return null;
        },
      },
    },
    transformValues: values => {
      type SerializedSection = {
        title: string;
        content: string;
        attachmentIds: string[];
      };

      const allSectionsEmpty = values.sections.every(isSectionEmpty);
      let serializedSections: SerializedSection[];

      if (values.attachment && allSectionsEmpty) {
        serializedSections = [];
      } else {
        serializedSections = values.sections
          .filter(section => !isSectionEmpty(section))
          .map(section => ({
            title: section.title,
            content: JSON.stringify(section.content),
            attachmentIds: section.attachments
              .map(attachment => attachment.fileId)
              .filter((id): id is string => id !== undefined),
          }));
      }

      const { attachment, ...valueWithoutAttachment } = values;

      return {
        ...valueWithoutAttachment,
        attachmentId: attachment?.fileId,
        sections: serializedSections,
      };
    },
  });

  const addSection = () => {
    form.insertListItem('sections', {
      title: '',
      content: EMPTY_PARAGRAPH,
      localId: nanoid(),
      attachments: [],
    });
  };

  const removeSection = (index: number) => {
    form.removeListItem('sections', index);
  };

  return { form, addSection, removeSection };
};
